import React, { useState, useEffect } from 'react';
import { useTranslation } from '../i18n/useTranslation.js';
import CodeEditor from './common/CodeEditor.js';
import SqlEditor from './common/SqlEditor.js';

import type { AuditEntityType, ChildStep, TaskStep } from '../api/types.js';

export type { ChildStep, TaskStep };

export interface DebuggingModalProps {
  open: boolean;
  onClose: () => void;
  entityType: AuditEntityType;
  entityId: string;
  additionalMsgs?: Array<{ stepName: string; msg: string }>;
  fetchTaskSteps: (entityType: AuditEntityType, entityId: string) => Promise<{ rows: TaskStep[]; total_count: number }>;
  onOpenErrorDetails?: (errorDetails: Record<string, any>) => void;
}

const STAGES = ['apps', 'connections', 'locations', 'similar', 'age+gender', 'zones', 'neighbors'];
const PAGE_SIZE = 10;

function fmtDateTime(iso?: string): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return iso;
  }
}

function fmtDate(iso?: string): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

function fmtTime(iso?: string): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return iso;
  }
}

function stateColor(state: string, errorMsg?: string): string {
  const s = (state ?? '').toUpperCase();
  if (s === 'FAILED') return errorMsg === 'No data found' ? '#4dabf7' : 'var(--red)';
  if (s === 'SUCCEEDED') return '#51cf66';
  if (s === 'RUNNING') return '#4dabf7';
  return 'var(--t2)';
}

type SortCol = 'taskId' | 'startedAt' | 'finishedAt';

function DebuggingModal({
  open,
  onClose,
  entityType,
  entityId,
  additionalMsgs,
  fetchTaskSteps,
  onOpenErrorDetails,
}: DebuggingModalProps) {
  const { t } = useTranslation();

  const [taskSteps, setTaskSteps] = useState<TaskStep[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState<TaskStep | null>(null);
  const [selectedStage, setSelectedStage] = useState('');
  const [filterText, setFilterText] = useState('');
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<SortCol>('startedAt');
  const [sortDesc, setSortDesc] = useState(true);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setTaskSteps([]);
    setSelectedTask(null);
    setSelectedStage('');
    setFilterText('');
    setPage(1);
    fetchTaskSteps(entityType, entityId)
      .then(({ rows }) => {
        setTaskSteps(rows);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [open, entityType, entityId]);

  if (!open) return null;

  const filtered = taskSteps.filter((row) => {
    const matchesFilter = !filterText.trim() || row.taskId.toLowerCase().includes(filterText.trim().toLowerCase());
    const matchesStage =
      entityType !== 'PROFILE' || !selectedStage || row.childSteps.some((cs) => cs.name === selectedStage);
    return matchesFilter && matchesStage;
  });

  const sorted = [...filtered].sort((a, b) => {
    const av = sortBy === 'taskId' ? a.taskId : sortBy === 'startedAt' ? a.startedAt ?? '' : a.finishedAt ?? '';
    const bv = sortBy === 'taskId' ? b.taskId : sortBy === 'startedAt' ? b.startedAt ?? '' : b.finishedAt ?? '';
    return sortDesc ? bv.localeCompare(av) : av.localeCompare(bv);
  });

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const curPage = Math.min(page, totalPages);
  const pageRows = sorted.slice((curPage - 1) * PAGE_SIZE, curPage * PAGE_SIZE);

  const toggleSort = (col: SortCol) => {
    if (sortBy === col) setSortDesc((d) => !d);
    else {
      setSortBy(col);
      setSortDesc(true);
    }
  };

  const arrow = (col: SortCol) => (sortBy === col ? (sortDesc ? ' ↓' : ' ↑') : '');

  const handleRowClick = (task: TaskStep) => {
    setSelectedTask((prev) => (prev?._id === task._id ? null : task));
    setTimeout(() => {
      document.getElementById('dbg-timeline')?.scrollIntoView({ behavior: 'smooth' });
    }, 60);
  };

  const renderStep = (stepData: ChildStep) => {
    const funcName = stepData.statement?.split('\n')[0];
    const additionalMsg = additionalMsgs?.find((m) => m.stepName === stepData.name)?.msg;
    const sqlBody = funcName
      ? stepData.statement
          ?.split(funcName)
          .slice(1)
          .join(funcName)
          .replace(/\n {12}/g, '\n')
          .replace(/^\n/, '')
      : undefined;

    return (
      <div key={stepData._id} style={{ marginBottom: 10 }}>
        {funcName && <p style={S.stepFunc}>{funcName}</p>}
        {stepData.startedAt && (
          <p style={S.stepMeta}>{t('debugging.steps.started', { date: fmtDateTime(stepData.startedAt) })}</p>
        )}
        {stepData.finishedAt && (
          <p style={S.stepMeta}>{t('debugging.steps.finished', { date: fmtDateTime(stepData.finishedAt) })}</p>
        )}
        {stepData.resultCount !== undefined && (
          <p style={S.stepMeta}>{t('debugging.steps.resultCount', { count: stepData.resultCount })}</p>
        )}
        {stepData.note && <p style={S.stepMeta}>{stepData.note}</p>}
        {additionalMsg && <p style={S.stepMeta}>{additionalMsg}</p>}
        {stepData.params && (
          <details style={S.details}>
            <summary style={S.summary}>{t('debugging.steps.params')}</summary>
            <CodeEditor value={stepData.params} />
          </details>
        )}
        {stepData.result && (
          <details style={S.details} open>
            <summary style={S.summary}>{t('debugging.steps.result')}</summary>
            <CodeEditor value={stepData.result} />
          </details>
        )}
        {stepData.errorDetails && Object.keys(stepData.errorDetails).length > 0 && onOpenErrorDetails && (
          <button className="btn" style={S.btnXs} onClick={() => onOpenErrorDetails(stepData.errorDetails!)}>
            {t('debugging.steps.errorDetails')}
          </button>
        )}
        {sqlBody && (
          <SqlEditor
            sqlQuery={sqlBody}
            hint={
              stepData.statement?.includes('day_of_week(p_date)-1') ? 'debugging.queries.daysOfWeekHint' : undefined
            }
          />
        )}
      </div>
    );
  };

  const renderTimeline = () => {
    if (!selectedTask) return null;
    const sameDay = !selectedTask.finishedAt || fmtDate(selectedTask.startedAt) === fmtDate(selectedTask.finishedAt);
    const dateLabel = sameDay
      ? fmtDate(selectedTask.startedAt)
      : `${fmtDate(selectedTask.startedAt)} – ${fmtDate(selectedTask.finishedAt)}`;

    return (
      <div style={S.timeline}>
        {selectedTask.childSteps.length > 0 && (
          <>
            <div style={S.tlLabel}>{dateLabel}</div>
            {selectedTask.childSteps.map((step) => (
              <div key={step._id} style={S.tlItem}>
                <div style={S.tlTime}>{fmtTime(step.startedAt)}</div>
                <div style={S.tlDot} />
                <div style={S.tlBody}>
                  <div style={S.tlStepName}>{step.name}</div>
                  {step.childSteps?.length ? step.childSteps.map((cs) => renderStep(cs)) : renderStep(step)}
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    );
  };

  return (
    <div className="mov" onClick={onClose}>
      <div className="mod" style={S.modXl} onClick={(e) => e.stopPropagation()}>
        <div className="mhd">
          <span className="mt">{t('debugging.title', { type: entityType, id: entityId })}</span>
          <div className="mx" onClick={onClose}>
            ×
          </div>
        </div>

        <div className="mbd">
          <div className="msec">
            <div className="mst">{t('debugging.table.title')}</div>

            <div style={S.filterRow}>
              {entityType === 'PROFILE' && (
                <select
                  className="fsl"
                  style={{ flex: '0 0 160px' }}
                  value={selectedStage}
                  disabled={loading}
                  onChange={(e) => {
                    setSelectedStage(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">{t('debugging.table.stagePlaceholder')}</option>
                  {STAGES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              )}
              <input
                className="fi"
                style={{ flex: 1 }}
                placeholder={t('debugging.table.filterPlaceholder')}
                value={filterText}
                disabled={loading}
                onChange={(e) => {
                  setFilterText(e.target.value);
                  setPage(1);
                }}
              />
            </div>

            <div style={S.tableWrap}>
              {loading ? (
                <div style={S.centred}>{t('debugging.loading')}</div>
              ) : (
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={{ ...S.th, cursor: 'pointer' }} onClick={() => toggleSort('taskId')}>
                        {t('debugging.table.colTaskId')}
                        {arrow('taskId')}
                      </th>
                      <th style={S.th}>{t('debugging.table.colState')}</th>
                      <th style={S.th}>{t('debugging.table.colSteps')}</th>
                      <th style={{ ...S.th, cursor: 'pointer' }} onClick={() => toggleSort('startedAt')}>
                        {t('debugging.table.colStartedAt')}
                        {arrow('startedAt')}
                      </th>
                      <th style={{ ...S.th, cursor: 'pointer' }} onClick={() => toggleSort('finishedAt')}>
                        {t('debugging.table.colFinishedAt')}
                        {arrow('finishedAt')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.length === 0 && (
                      <tr>
                        <td colSpan={5} style={S.centred}>
                          {t('debugging.table.noResults')}
                        </td>
                      </tr>
                    )}
                    {pageRows.map((row) => {
                      const isNoData =
                        row.state?.toUpperCase() === 'FAILED' && row.errorDetails?.message === 'No data found';
                      const displayState = isNoData ? 'NO_DATA' : row.state;
                      const color = stateColor(row.state, row.errorDetails?.message);
                      const isSelected = selectedTask?._id === row._id;

                      return (
                        <tr
                          key={row._id}
                          style={{ ...S.tr, ...(isSelected ? S.trSel : {}) }}
                          onClick={() => handleRowClick(row)}
                        >
                          <td style={{ ...S.td, fontFamily: 'var(--mono)', fontSize: 11 }}>{row.taskId}</td>
                          <td style={{ ...S.td, color, fontWeight: 600, fontSize: 11 }}>{displayState}</td>
                          <td style={S.td}>
                            {row.childSteps.map((cs, i) => (
                              <span
                                key={i}
                                style={
                                  i < row.childSteps.length - 1
                                    ? {
                                        ...S.pill,
                                        ...S.pillBorderBottom,
                                      }
                                    : S.pill
                                }
                              >
                                {cs.name}
                              </span>
                            ))}
                          </td>
                          <td style={{ ...S.td, fontFamily: 'var(--mono)', fontSize: 11 }}>
                            {fmtDateTime(row.startedAt)}
                          </td>
                          <td style={{ ...S.td, fontFamily: 'var(--mono)', fontSize: 11 }}>
                            {fmtDateTime(row.finishedAt)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {!loading && totalPages > 1 && (
              <div style={S.pager}>
                <button
                  className="btn"
                  style={S.pageBtn}
                  disabled={curPage === 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  ←
                </button>
                <span style={S.pageInfo}>
                  {curPage} / {totalPages}
                </span>
                <button
                  className="btn"
                  style={S.pageBtn}
                  disabled={curPage === totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  →
                </button>
              </div>
            )}
          </div>

          {selectedTask?.params && (
            <div className="msec">
              <details style={S.details} open>
                <summary style={{ ...S.summary, fontWeight: 600, fontSize: 12 }}>
                  {t('debugging.steps.entryPointParams')}
                </summary>
                <CodeEditor value={selectedTask.params} />
              </details>
            </div>
          )}

          {selectedTask && (
            <div id="dbg-timeline" className="msec">
              <div className="mst">{t('debugging.timeline.title')}</div>
              {renderTimeline()}
            </div>
          )}
        </div>

        <div className="mft">
          <button className="btn btn-p" onClick={onClose}>
            {t('debugging.close')}
          </button>
        </div>
      </div>
    </div>
  );
}

const S: Record<string, React.CSSProperties> = {
  modXl: { width: 'min(94vw, 1100px)', maxHeight: '88vh' },
  filterRow: { display: 'flex', gap: 8, marginBottom: 10 },
  tableWrap: {
    overflowX: 'auto',
    borderRadius: 6,
    border: '1px solid var(--bs)',
    marginBottom: 4,
  },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 12 },
  th: {
    padding: '7px 10px',
    textAlign: 'left' as const,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '.08em',
    textTransform: 'uppercase' as const,
    color: 'var(--t3)',
    background: 'var(--bg-deep)',
    borderBottom: '1px solid var(--bs)',
    userSelect: 'none' as const,
    whiteSpace: 'nowrap' as const,
  },
  td: {
    padding: '7px 10px',
    borderBottom: '1px solid var(--bs)',
    color: 'var(--t2)',
    verticalAlign: 'top' as const,
  },
  tr: { cursor: 'pointer', transition: 'background .12s' },
  trSel: { background: 'var(--gd)' },
  centred: { padding: 20, textAlign: 'center' as const, color: 'var(--t3)', fontSize: 12 },
  pill: {
    display: 'block',
    fontSize: 11,
    padding: '1px 5px',
    borderRadius: 3,
    marginRight: 3,
    marginBottom: 2,
  },
  pillBorderBottom: {
    borderBottom: '1px solid var(--bs)',
  },
  pager: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    marginTop: 8,
  },
  pageBtn: { padding: '3px 10px', fontSize: 13, lineHeight: 1 },
  pageInfo: { fontSize: 11, color: 'var(--t2)', fontFamily: 'var(--mono)' },
  details: { marginBottom: 6 },
  summary: {
    cursor: 'pointer',
    fontSize: 11,
    color: 'var(--t2)',
    padding: '5px 9px',
    background: 'var(--bg-card)',
    borderRadius: 4,
    border: '1px solid var(--bs)',
    userSelect: 'none' as const,
  },
  btnXs: { fontSize: 11, padding: '3px 8px', marginRight: 6, marginBottom: 4 },
  timeline: { paddingTop: 4 },
  tlLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: 'var(--gold)',
    fontFamily: 'var(--mono)',
    letterSpacing: '.06em',
    marginBottom: 12,
    paddingBottom: 6,
    borderBottom: '1px solid var(--bs)',
  },
  tlItem: { display: 'flex', gap: 10, marginBottom: 18 },
  tlTime: {
    flex: '0 0 76px',
    fontSize: 10,
    fontFamily: 'var(--mono)',
    color: 'var(--t3)',
    paddingTop: 3,
    textAlign: 'right' as const,
  },
  tlDot: {
    flexShrink: 0,
    width: 10,
    height: 10,
    borderRadius: '50%',
    background: 'var(--gold)',
    border: '2px solid var(--bg-panel)',
    marginTop: 3,
  },
  tlBody: { flex: 1, minWidth: 0 },
  tlStepName: { fontSize: 12, fontWeight: 600, color: 'var(--t1)', marginBottom: 6 },
  stepFunc: { fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--gold)', marginBottom: 3 },
  stepMeta: { fontSize: 11, color: 'var(--t2)', marginBottom: 2 },
};

export default DebuggingModal;
